import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

import { AmountFormatterService } from 'src/sale-item/amount-formatter.service';
import { PaymentPlanTextService } from '../payment-plan-text.service';
import { SeFeThemesLoader } from 'se-fe-themes';

@Component({
  selector: 'app-payment-plan-text',
  styleUrls: ['./payment-plan-text.component.scss'],
  templateUrl: './payment-plan-text.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PaymentPlanTextComponent implements OnInit, OnDestroy {

  upfrontAmountOption: string;
  itemForm: FormGroup;
  error = false;
  defaultState = true;
  paymentPlanTextSubscription: Subscription;
  upfrontAmount: string;
  paymentPlanAmount: string;
  priceVariations: FormGroup[] = [];

  constructor(
    private amountFormatter: AmountFormatterService,
    private paymentPlanTextService: PaymentPlanTextService
  ) {
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com'
    });
  }

  ngOnInit(): void {
    this.subscribeToPaymentPlanTextOptions();
  }

  ngOnDestroy(): void {
    this.paymentPlanTextSubscription.unsubscribe();
  }

  public subscribeToPaymentPlanTextOptions(): void {
    this.paymentPlanTextSubscription = this.paymentPlanTextService.$paymentPlanText
      .subscribe((paymentPlanTextOptions: any) => {
        this.itemForm = paymentPlanTextOptions.itemForm;
        this.upfrontAmountOption = paymentPlanTextOptions.upfrontAmountOption;
        this.defaultState = paymentPlanTextOptions.defaultState;
        this.priceVariations = paymentPlanTextOptions?.priceVariations;
        this.priceVariations?.forEach((priceVariation: FormGroup) => {
          priceVariation.valueChanges.subscribe(() => {
            this.setPaymentPlanText();
          });
        });
        if (this.itemForm) { this.setPaymentPlanText(); }
      });
  }

  public setPaymentPlanText(): void {
    this.setErrorFlag();
    if (!this.error) { this.setValidText(); }
  }

  public getDefaultPrice() {
    return parseFloat(this.upfrontAmount.substring(1)) + parseFloat(this.paymentPlanAmount.substring(1));
  }

  private setValidText(): void {
    if (this.upfrontAmountOption === 'fullAmount') {
      this.setFullAmountText();
    } else if (this.upfrontAmountOption === 'partialAmount') {
      this.setPartialAmountText();
    } else if (this.upfrontAmountOption === 'zeroAmount') {
      this.setZeroAmountText();
    }
  }

  private setFullAmountText(): void {
    this.upfrontAmount = this.priceControl().value ? `${this.formattedAmount(this.sanitizedPrice())}` : 'Full price';
  }

  private setZeroAmountText(): void {
    this.paymentPlanAmount = this.formattedAmount(this.sanitizedPrice());
    this.upfrontAmount = this.formattedAmount(0);
  }

  private setPartialAmountText(): void {
    let upfrontAmount = this.sanitizedUpfrontAmount();
    let paymentPlanAmount = this.sanitizedPrice();
    if (upfrontAmount) {
      paymentPlanAmount  = paymentPlanAmount - upfrontAmount;
    } else {
      upfrontAmount = 0;
    }
    this.upfrontAmount = this.formattedAmount(upfrontAmount);
    this.paymentPlanAmount = this.formattedAmount(paymentPlanAmount);
  }

  private isUpfrontAmountInValid(): boolean {
    if (this.upfrontAmountOption === 'partialAmount') {
      return !!this.upfrontAmountControl().errors;
    } else {
      return false;
    }
  }

  private sanitizedPrice(): number {
    return this.amountFormatter.sanitizePrice(this.priceControl().value);
  }

  private sanitizedUpfrontAmount(): number {
    return this.amountFormatter.sanitizePrice(this.upfrontAmountControl().value);
  }

  private priceControl(): AbstractControl {
    return this.itemForm.get('price');
  }

  private upfrontAmountControl(): AbstractControl {
    return this.itemForm.get('upfrontAmount');
  }

  private formattedAmount(amount: string | number) {
    return this.amountFormatter.formattedAmount(amount, true);
  }

  private setErrorFlag(): void {
    this.error = !this.defaultState && (!!this.priceControl().errors || this.isUpfrontAmountInValid());
  }
}
